import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { Note, Tag } from '../../../_service/data.service';
import { UserSettingsService } from '../../../_service/user-settings.service';
import { FilterParams } from '../../../_service/filter.service';

type QuickCard = {
  id: string;
  title: string;
  color: string;
  isExpanded: boolean;
  kind: 'notes' | 'tags';
};

type RelatedTagStat = {
  tagId: string;
  label: string;
  count: number;
};

const STORAGE_KEY = 'quick-access-card-expanded';

@Component({
  selector: 'app-quick-access',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './quick-access.component.html',
  styleUrls: ['./quick-access.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class QuickAccessComponent implements OnInit, OnChanges {
  @Input() notes: Note[] = [];
  @Input() filteredNotes: Note[] = [];
  @Input() availableTags: Tag[] = [];
  @Input() filter: FilterParams = {};
  @Input() isSelectMode = false;
  @Input() selectedNoteIds: string[] = [];
  @Output() collapsePanel = new EventEmitter<void>();
  @Output() selectedNoteIdsChange = new EventEmitter<string[]>();
  @Output() relatedTagSelected = new EventEmitter<string>();
  @Output() relatedTagRemoved = new EventEmitter<string>();
  @Output() relatedTagExcluded = new EventEmitter<string>();

  cards: QuickCard[] = [];
  private favoriteNoteIds: string[] = [];

  constructor(
    private router: Router,
    private userSettings: UserSettingsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userSettings.userSettings$.subscribe((settings) => {
      this.favoriteNoteIds = Array.isArray(settings?.favoriteNoteIds)
        ? settings.favoriteNoteIds.filter((id: unknown) => typeof id === 'string')
        : [];
      this.buildCards();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notes']) {
      this.buildCards();
    }
  }

  get areAllCardsExpanded(): boolean {
    return this.cards.length > 0 && this.cards.every((card) => card.isExpanded);
  }

  buildCards(): void {
    this.cards = [
      {
        id: 'favorites',
        title: 'Favorites ❤️',
        color: '#f59e0b',
        isExpanded: this.getExpandedState('favorites', true),
        kind: 'notes',
      },
      {
        id: 'related-tags',
        title: 'Related Tags 🏷️',
        color: 'var(--primary-color, var(--p-primary-color, #2563eb))',
        isExpanded: this.getExpandedState('related-tags', true),
        kind: 'tags',
      },
      {
        id: 'recent',
        title: 'Recent ⏰',
        color: '#3b82f6',
        isExpanded: this.getExpandedState('recent', true),
        kind: 'notes',
      },
      {
        id: 'new-this-week',
        title: 'New This Week 🆕',
        color: '#10b981',
        isExpanded: this.getExpandedState('new-this-week', true),
        kind: 'notes',
      },
    ];
  }

  getNotesForCard(cardId: string): Note[] {
    const now = new Date();
    switch (cardId) {
      case 'favorites': {
        const favSet = new Set(this.favoriteNoteIds);
        return this.notes
          .filter((n) => favSet.has(n._id))
          .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
      }
      case 'recent': {
        return this.notes
          .filter((n) => {
            const diffDays = (now.getTime() - new Date(n.last_modified).getTime()) / 86_400_000;
            return diffDays <= 5;
          })
          .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
      }
      case 'new-this-week': {
        return this.notes
          .filter((n) => {
            const diffDays = (now.getTime() - new Date(n.created_on).getTime()) / 86_400_000;
            return diffDays <= 7;
          })
          .sort((a, b) => Date.parse(b.created_on) - Date.parse(a.created_on));
      }
      default:
        return [];
    }
  }

  getRelatedTags(): RelatedTagStat[] {
    const selectedTagIds = new Set(Array.isArray(this.filter.TagIds) ? this.filter.TagIds : []);
    const excludedTagIds = new Set(Array.isArray(this.filter.ExcludeTagIds) ? this.filter.ExcludeTagIds : []);
    const tagById = new Map(this.availableTags.map((tag) => [tag._id, tag]));
    const counter = new Map<string, number>();

    for (const note of this.filteredNotes) {
      for (const tagId of this.extractTagIds(note, tagById)) {
        if (!tagId || selectedTagIds.has(tagId) || excludedTagIds.has(tagId)) {
          continue;
        }
        counter.set(tagId, (counter.get(tagId) ?? 0) + 1);
      }
    }

    return Array.from(counter.entries())
      .map(([tagId, count]) => {
        const knownTag = tagById.get(tagId);
        return {
          tagId,
          label: knownTag?.name ?? tagId,
          count,
        };
      })
      .sort((left, right) => {
        if (right.count !== left.count) {
          return right.count - left.count;
        }
        return left.label.localeCompare(right.label);
      })
      .slice(0, 10);
  }

  getSelectedRelatedTags(): RelatedTagStat[] {
    const selectedTagIds = Array.isArray(this.filter.TagIds)
      ? this.filter.TagIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    if (!selectedTagIds.length) {
      return [];
    }

    // Count from ALL notes (unfiltered) so the count is meaningful regardless
    // of how many tags are currently active in the AND filter.
    const tagById = new Map(this.availableTags.map((tag) => [tag._id, tag]));
    const selectedSet = new Set(selectedTagIds);
    const counter = new Map<string, number>();
    for (const note of this.notes) {
      for (const tagId of this.extractTagIds(note, tagById)) {
        if (selectedSet.has(tagId)) {
          counter.set(tagId, (counter.get(tagId) ?? 0) + 1);
        }
      }
    }

    return selectedTagIds.map((tagId) => ({
      tagId,
      label: tagById.get(tagId)?.name ?? tagId,
      count: counter.get(tagId) ?? 0,
    }));
  }

  getTagsCardCount(): number {
    return this.getSelectedRelatedTags().length + this.getRelatedTags().length;
  }

  addRelatedTag(tagId: string, event?: Event): void {
    event?.stopPropagation();
    if (!tagId) {
      return;
    }
    this.relatedTagSelected.emit(tagId);
  }

  removeRelatedTag(tagId: string, event?: Event): void {
    event?.stopPropagation();
    if (!tagId) {
      return;
    }
    this.relatedTagRemoved.emit(tagId);
  }

  excludeRelatedTag(tagId: string, event?: Event): void {
    event?.stopPropagation();
    if (!tagId) {
      return;
    }
    this.relatedTagExcluded.emit(tagId);
  }

  toggleCardExpanded(card: QuickCard, event?: Event): void {
    event?.stopPropagation();
    card.isExpanded = !card.isExpanded;
    this.saveExpandedState(card.id, card.isExpanded);
  }

  toggleAllCardsExpanded(event?: Event): void {
    event?.stopPropagation();
    const nextExpandedState = !this.areAllCardsExpanded;
    this.cards = this.cards.map((card) => ({
      ...card,
      isExpanded: nextExpandedState,
    }));
    this.saveAllExpandedStates(nextExpandedState);
  }

  clickRow(note: Note): void {
    if (this.isSelectMode) {
      this.toggleSelection(note);
      return;
    }
    this.router.navigate(['view', note._id], { state: { filter: this.filter } });
  }

  isNoteSelected(id: string | undefined): boolean {
    if (!id) {
      return false;
    }
    return this.selectedNoteIds.includes(id);
  }

  toggleSelection(note: Note, event?: Event): void {
    event?.stopPropagation();
    const id = note?._id;
    if (!id) {
      return;
    }

    const isSelected = this.selectedNoteIds.includes(id);
    const nextSelection = isSelected
      ? this.selectedNoteIds.filter((noteId) => noteId !== id)
      : [...this.selectedNoteIds, id];

    this.selectedNoteIdsChange.emit(nextSelection);
  }

  areAllCardNotesSelected(cardId: string): boolean {
    const ids = this.getSelectableCardNoteIds(cardId);
    if (!ids.length) {
      return false;
    }

    const selectedSet = new Set(this.selectedNoteIds);
    return ids.every((id) => selectedSet.has(id));
  }

  toggleCardSelection(cardId: string, event?: Event): void {
    event?.stopPropagation();
    const cardNoteIds = this.getSelectableCardNoteIds(cardId);
    if (!cardNoteIds.length) {
      return;
    }

    const selectedSet = new Set(this.selectedNoteIds);
    const allSelected = cardNoteIds.every((id) => selectedSet.has(id));

    if (allSelected) {
      const cardSet = new Set(cardNoteIds);
      this.selectedNoteIdsChange.emit(this.selectedNoteIds.filter((id) => !cardSet.has(id)));
      return;
    }

    const nextSelection = Array.from(new Set([...this.selectedNoteIds, ...cardNoteIds]));
    this.selectedNoteIdsChange.emit(nextSelection);
  }

  collapse(): void {
    this.collapsePanel.emit();
  }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);
    let totalMonths =
      (now.getFullYear() - modified.getFullYear()) * 12 + (now.getMonth() - modified.getMonth());
    if (now.getDate() < modified.getDate()) {
      totalMonths--;
    }
    return totalMonths > 0 ? `${totalMonths}m: ` : '';
  }

  getAttachmentFlag(note: Note): string {
    return Array.isArray(note.Attachments) && note.Attachments.length > 0 ? ' 📎' : '';
  }

  trackByCardId(_index: number, card: QuickCard): string {
    return card.id;
  }

  trackByNoteId(_index: number, note: Note): string {
    return note._id;
  }

  trackByRelatedTagId(_index: number, relatedTag: RelatedTagStat): string {
    return relatedTag.tagId;
  }

  private extractTagIds(note: Note, tagById: Map<string, Tag>): string[] {
    if (!Array.isArray((note as any)?.Tags)) {
      return [];
    }

    const resolved = new Set<string>();
    for (const rawTag of (note as any).Tags) {
      if (typeof rawTag === 'string') {
        const value = rawTag.trim();
        if (!value) {
          continue;
        }
        if (tagById.has(value)) {
          resolved.add(value);
          continue;
        }

        const lowered = value.toLocaleLowerCase();
        const byName = this.availableTags.find((tag) => tag.name.toLocaleLowerCase() === lowered || tag.slug === lowered);
        if (byName?._id) {
          resolved.add(byName._id);
          continue;
        }

        resolved.add(value);
        continue;
      }

      const tagId = String((rawTag as any)?._id ?? '').trim();
      if (tagId) {
        resolved.add(tagId);
      }
    }

    return Array.from(resolved);
  }

  private getSelectableCardNoteIds(cardId: string): string[] {
    return this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);
  }

  private saveExpandedState(id: string, expanded: boolean): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const state = raw ? JSON.parse(raw) : {};
      state[id] = expanded;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }

  private saveAllExpandedStates(expanded: boolean): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const state = raw ? JSON.parse(raw) : {};
      for (const card of this.cards) {
        state[card.id] = expanded;
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }

  private getExpandedState(id: string, defaultValue: boolean): boolean {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const state = JSON.parse(raw);
        return state[id] !== undefined ? state[id] : defaultValue;
      }
    } catch {
      // ignore storage errors
    }
    return defaultValue;
  }
}
